/**
 * Utility for Tally-style Unit and Quantity Calculations
 */

/**
 * Parses a Tally-style quantity input.
 * Supports:
 * - Hyphenated input: "1-5" (1 main, 5 base)
 * - Plain number: "15" (treated as base units if it's a compound unit and no dots)
 * - Decimal number: "1.5" (1.5 main units)
 * 
 * @param {string|number} input - The input value
 * @param {number} conversionFactor - The conversion factor (e.g., 10 for Box of 10 Nos)
 * @param {boolean} isCompound - Whether the unit is compound
 * @returns {number} - Total base units
 */
export const parseTallyQuantity = (input, conversionFactor, isCompound = true) => {
    if (input === null || input === undefined || input === '') return 0;

    const inputStr = String(input).trim();
    if (inputStr === '') return 0;

    // Handle hyphenated input: 1-5
    if (inputStr.includes('-')) {
        const parts = inputStr.split('-');
        const mainUnits = parseFloat(parts[0]) || 0;
        const baseUnits = parseFloat(parts[1]) || 0;
        return (mainUnits * conversionFactor) + baseUnits;
    }

    const numValue = parseFloat(inputStr.replace(/,/g, ''));
    if (isNaN(numValue)) return 0;

    // Treat as main units (multiplier)
    return numValue * conversionFactor;
};

/**
 * Calculates total base units by intelligently parsing quantity string with explicit units.
 * For example: "1.5 BOX" where 1 BOX = 10 LTR returns 15 (LTR).
 * 
 * @param {string|number} input - The input quantity string, e.g. "1.5 BOX" or "15"
 * @param {Object} data - The stock item configuration object containing unit relationships
 * @returns {number} - Total base units
 */
export const calculateBaseUnitsFromInput = (input, data) => {
    if (input === null || input === undefined || input === '') return 0;
    const inputStr = String(input).trim();
    if (inputStr === '') return 0;

    const unitInfo = parseUnitString(data?.unit_name);
    const mainUnitFactor = unitInfo?.factor || 1;
    const isCompound = mainUnitFactor > 1;

    // Separate number from potential unit string
    const match = inputStr.match(/^([\d.,-]+)\s*(.*)$/i);
    if (!match) return parseTallyQuantity(inputStr, mainUnitFactor, isCompound);

    const qtyStr = match[1];
    const explicitlyTypedUnit = match[2] ? match[2].trim().toLowerCase() : '';

    if (!explicitlyTypedUnit) {
        return parseTallyQuantity(qtyStr, mainUnitFactor, isCompound);
    }

    const mainUnitName = (unitInfo?.mainUnit || data?.unit_name || '').toLowerCase();
    const baseUnitName = (unitInfo?.baseUnit || '').toLowerCase();

    const altUnitInfo = parseUnitString(data?.alt_unit_name);
    const altMainUnitName = (altUnitInfo?.mainUnit || data?.alt_unit_name || '').toLowerCase();
    const altBaseUnitName = (altUnitInfo?.baseUnit || '').toLowerCase();

    // Match conditions
    if (mainUnitName && explicitlyTypedUnit === mainUnitName) {
        return parseTallyQuantity(qtyStr, mainUnitFactor, false);
    }
    else if (baseUnitName && explicitlyTypedUnit === baseUnitName) {
        // Base unit was explicitly typed
        return parseTallyQuantity(qtyStr, 1, false);
    }
    else if (altMainUnitName && altMainUnitName !== 'not applicable' && explicitlyTypedUnit === altMainUnitName) {
        const altQty = parseTallyQuantity(qtyStr, altUnitInfo?.factor || 1, (altUnitInfo?.factor || 1) > 1);

        // Convert to main base units
        const unit_count = parseFloat(data?.where_unit) || 1;
        const alt_unit_count = parseFloat(data?.where_alternate_unit) || 1;

        const whereUnitName = (data?.where_unit_name || '').toLowerCase();
        let whereFactor = mainUnitFactor;
        if (baseUnitName && whereUnitName === baseUnitName) {
            whereFactor = 1;
        }

        const sourceQtyInWhereUnits = (altQty / alt_unit_count) * unit_count;
        return sourceQtyInWhereUnits * whereFactor;
    }
    else if (altBaseUnitName && explicitlyTypedUnit === altBaseUnitName) {
        const altQty = parseTallyQuantity(qtyStr, 1, false);
        const unit_count = parseFloat(data?.where_unit) || 1;
        const alt_unit_count = parseFloat(data?.where_alternate_unit) || 1;

        const whereUnitName = (data?.where_unit_name || '').toLowerCase();
        let whereFactor = mainUnitFactor;
        if (baseUnitName && whereUnitName === baseUnitName) {
            whereFactor = 1;
        }

        const sourceQtyInWhereUnits = (altQty / alt_unit_count) * unit_count;
        return sourceQtyInWhereUnits * whereFactor;
    }

    // Default processing if the unit text doesn't match perfectly
    return parseTallyQuantity(qtyStr, mainUnitFactor, isCompound);
};

/**
 * Formats base units into a Tally-style string.
 * Result: "1-5" or "15"
 * 
 * @param {number} totalBaseUnits - Total quantity in base units
 * @param {number} conversionFactor - The conversion factor
 * @param {number} decimalPlaces - Number of decimal places to show (from unit config)
 * @returns {string} - Formatted quantity
 */
export const formatTallyQuantity = (totalBaseUnits, conversionFactor, decimalPlaces = 0) => {
    const precision = parseInt(decimalPlaces) || 0;
    if (totalBaseUnits === 0) return (0).toFixed(precision);
    if (!totalBaseUnits || isNaN(totalBaseUnits)) return (0).toFixed(precision);

    // If no conversion factor or factor is 1, return as decimal
    if (!conversionFactor || conversionFactor <= 1) {
        return totalBaseUnits.toFixed(precision);
    }

    // Calculate main units and remainder
    // Use a small epsilon to handle floating point issues
    const roundedTotal = Math.round(totalBaseUnits * Math.pow(10, precision)) / Math.pow(10, precision);
    const mainUnits = Math.floor(roundedTotal / conversionFactor);
    const remainder = Math.round((roundedTotal % conversionFactor) * Math.pow(10, precision)) / Math.pow(10, precision);

    if (mainUnits === 0) {
        return remainder.toFixed(precision);
    }

    if (remainder === 0) {
        return `${mainUnits}-0`; // Tally style often shows -0 or just main
    }

    // If remainder is whole number, show as integer in hyphenated form
    if (remainder === Math.floor(remainder)) {
        return `${mainUnits}-${Math.floor(remainder)}`;
    }

    return `${mainUnits}-${remainder.toFixed(precision)}`;
};

/**
 * Calculates display name with units: "1-5 Box"
 * @param {number} totalBaseUnits 
 * @param {number} conversionFactor 
 * @param {string} unitName 
 * @param {number} decimalPlaces 
 * @returns {string}
 */
export const formatQuantityWithUnit = (totalBaseUnits, conversionFactor, unitName, decimalPlaces = 0) => {
    const formatted = formatTallyQuantity(totalBaseUnits, conversionFactor, decimalPlaces);
    return `${formatted} ${unitName}`;
};

/**
 * Formats base units into a detailed Tally-style string.
 * Result: "10 Box 0 LTR" or "10 Nos"
 * 
 * @param {number} totalBaseUnits - Total quantity in base units
 * @param {string} unitString - The unit string (e.g., "Box of 10 LTR")
 * @param {number} decimalPlaces - Number of decimal places
 * @returns {string} - Formatted quantity string
 */
export const formatQuantityLong = (totalBaseUnits, unitString, decimalPlaces = 0) => {
    const precision = parseInt(decimalPlaces) || 0;
    if (totalBaseUnits === 0) return `0 ${unitString}`;
    if (!totalBaseUnits || isNaN(totalBaseUnits)) return `0 ${unitString}`;

    const unitInfo = parseUnitString(unitString);
    if (!unitInfo) return `${totalBaseUnits.toFixed(precision)} ${unitString}`;

    if (unitInfo.type === 'Compound' && unitInfo.factor > 1) {
        const conversionFactor = unitInfo.factor;
        const roundedTotal = Math.round(totalBaseUnits * Math.pow(10, precision)) / Math.pow(10, precision);
        const mainUnits = Math.floor(roundedTotal / conversionFactor);
        const remainder = Math.round((roundedTotal % conversionFactor) * Math.pow(10, precision)) / Math.pow(10, precision);

        return `${mainUnits} ${unitInfo.mainUnit} ${remainder.toFixed(precision)} ${unitInfo.baseUnit}`;
    }

    return `${totalBaseUnits.toFixed(precision)} ${unitInfo.mainUnit}`;
};

/**
 * Parses a unit string to check if it's compound and extract conversion factor
 * Example: "Box of 10 Nos" -> { mainUnit: "Box", factor: 10, baseUnit: "Nos" }
 */
export const parseUnitString = (unitString) => {
    if (!unitString || unitString === 'Not Applicable') return null;

    // Ensure it's a string
    const unitStr = String(unitString).trim();
    if (!unitStr || unitStr === 'Not Applicable') return null;

    const compoundPattern = /(.+)\s+of\s+([0-9.]+)\s+(.+)/i;
    const match = unitStr.match(compoundPattern);

    if (match) {
        return {
            type: 'Compound',
            mainUnit: match[1]?.trim(),
            factor: parseFloat(match[2]),
            baseUnit: match[3]?.trim()
        };
    }

    return {
        type: 'Simple',
        mainUnit: unitStr,
        factor: 1,
        baseUnit: unitStr
    };
};

/**
 * Calculates alternate units based on total base units and the "Where" clause.
 * 
 * @param {number} totalBaseUnits - Total quantity in base units
 * @param {Object} data - The stock item data containing conversion factors
 * @returns {number} - Total alternate units
 */
export const calculateAltUnits = (totalBaseUnits, data) => {
    if (!totalBaseUnits || !data?.alt_unit_name || data.alt_unit_name === 'Not Applicable') return 0;

    const unit_count_in_relationship = parseFloat(data.where_unit) || 0; // e.g., 1 (for 1 Box)
    const alt_unit_count_in_relationship = parseFloat(data.where_alternate_unit) || 0; // e.g., 12 (for 12 NOS)

    if (unit_count_in_relationship === 0 || alt_unit_count_in_relationship === 0) return 0;

    const unitInfo = parseUnitString(data.unit_name);
    const mainUnitFactor = unitInfo?.factor || 1;
    const mainUnitName = (unitInfo?.mainUnit || '').toLowerCase();
    const whereUnitName = (data.where_unit_name || '').toLowerCase();

    let sourceQty = 0;
    if (whereUnitName && mainUnitName && whereUnitName === mainUnitName) {
        // Linked to Main Unit (e.g. Box)
        sourceQty = totalBaseUnits / mainUnitFactor;
    } else {
        // Linked to Base Unit (e.g. LTR) or simple unit
        sourceQty = totalBaseUnits;
    }

    return (sourceQty / unit_count_in_relationship) * alt_unit_count_in_relationship;
};
